import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useModule } from '../utils/useModule';
import { useTableFilters } from '../utils/useTableFilters';
import { deleteShiftLog, getPaginatedShiftLogs } from '../api/shift-logAPI';
import ShiftLogDataTable from './ShiftLogDataTable';
import ShiftLogManager from './ShiftLogManager';
import { getUserTimeZone } from '../shared/timezone';

const FIRST_PAGE_SIZE = 25;
const SUBSEQUENT_PAGE_SIZE = 5;

const ShiftLogLayer = () => {
  const userProfile = useUserProfile();
  const isShiftLogModuleEnabled = useModule('shiftlog');
  const privileges = Array.isArray(userProfile?.privileges) ? userProfile.privileges : [];
  const canViewShiftLogs = privileges.includes('shift-log:view') || privileges.includes('shift-log:dview');
  const canCreateShiftLogs = privileges.includes('shift-log:create');
  const canUpdateShiftLogs = privileges.includes('shift-log:update') || privileges.includes('shift-log:uodate');
  const canDeleteShiftLogs = privileges.includes('shift-log:delete');
  const timeZone = getUserTimeZone();

  const [searchTerm, setSearchTerm] = useState('');
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [logs, setLogs] = useState([]);
  const logsRef = useRef([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('inProgress');
  const listContainerRef = useRef(null);

  const [showManager, setShowManager] = useState(false);
  const [managerConfig, setManagerConfig] = useState({ shiftLogId: null, readOnly: false });

  const tableFilters = useMemo(() => [
    {
      field: 'departmentIds',
      type: 'dropDown',
      label: 'Site or Department',
      api: 'hierarchy-structures',
      displayField: 'name',
    },
    {
      field: 'userIds',
      type: 'dropDown',
      label: 'Supervisor',
      api: 'users',
      displayField: (user) => user.name || user.email || `User ${user.id}`,
      menuBarFilter: 1
    },
    { field: 'startDate', type: 'date', label: 'Report Start', menuBarFilter: 1 },
    { field: 'endDate', type: 'date', label: 'Report End', menuBarFilter: 1 },
  ], []);

  const resetPagination = useCallback(() => {
    logsRef.current = [];
    setLogs([]);
    setPage(1);
    setHasMore(true);
    setRefreshKey((prev) => prev + 1);
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
  }, []);

  const {
    appliedFilters,
    renderFilterControls,
  } = useTableFilters(tableFilters, {}, {
    onApply: resetPagination,
    onReset: resetPagination,
  });

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  useEffect(() => {
    logsRef.current = logs;
  }, [logs]);

  useEffect(() => {
    resetPagination();
  }, [activeTab, sortField, sortOrder, searchTerm, resetPagination]);

  useEffect(() => {
    let isMounted = true;

    const fetchLogs = async () => {
      if (!canViewShiftLogs || !isShiftLogModuleEnabled) {
        logsRef.current = [];
        setLogs([]);
        setHasMore(false);
        return;
      }

      setLoading(true);
      try {
        const pageSize = page === 1 ? FIRST_PAGE_SIZE : SUBSEQUENT_PAGE_SIZE;
        const additionalPages = FIRST_PAGE_SIZE / SUBSEQUENT_PAGE_SIZE;
        const apiPage = page === 1 ? 1 : page + additionalPages - 1;

        const params = {
          page: Math.max(Math.trunc(apiPage), 1),
          pageSize,
          ...appliedFilters,
          completed: activeTab === 'completed',
          search: searchTerm,
        };

        if (sortField) params.sortField = sortField;
        if (sortOrder) params.sortOrder = sortOrder;

        const response = await getPaginatedShiftLogs(params);
        const rows = Array.isArray(response?.data) ? response.data : [];
        const totalRaw = Number(response?.total);
        const total = Number.isFinite(totalRaw) ? totalRaw : undefined;

        const base = page === 1 ? [] : logsRef.current;
        const seen = new Set(base.map((item) => item.id));
        const merged = page === 1 ? [] : [...base];
        rows.forEach((row) => {
          if (!row || seen.has(row.id)) return;
          merged.push(row);
          seen.add(row.id);
        });

        if (!isMounted) return;
        logsRef.current = merged;
        setLogs(merged);
        if (total !== undefined) {
          setHasMore(merged.length < total);
        } else {
          setHasMore(rows.length === pageSize);
        }
      } catch (err) {
        console.error('Failed to load shift logs', err);
        if (!isMounted) return;
        if (page === 1) {
          logsRef.current = [];
          setLogs([]);
        }
        setHasMore(false);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLogs();

    return () => {
      isMounted = false;
    };
  }, [page, refreshKey, appliedFilters, activeTab, sortField, sortOrder, searchTerm, canViewShiftLogs, isShiftLogModuleEnabled]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 5 &&
        hasMore &&
        !loading
      ) {
        setPage((prev) => prev + 1);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading]);

  const handleSortChange = useCallback((field) => {
    let nextField = field;
    let nextOrder = 'asc';

    if (sortField === field) {
      if (sortOrder === 'asc') {
        nextOrder = 'desc';
      } else if (sortOrder === 'desc') {
        nextField = null;
        nextOrder = null;
      } else {
        nextOrder = 'asc';
      }
    }

    setSortField(nextField);
    setSortOrder(nextOrder);
  }, [sortField, sortOrder]);

  const handleTabChange = useCallback((tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
  }, [activeTab]);

  const handleStartShiftLog = useCallback(() => {
    setManagerConfig({ shiftLogId: null, readOnly: false });
    setShowManager(true);
  }, []);

  const handleOpenShiftLog = useCallback((log) => {
    if (!log) return;
    const completed = Number(log.completed) === 1;
    setManagerConfig({ shiftLogId: log.id, readOnly: completed });
    setShowManager(true);
  }, []);

  const handleViewShiftLog = useCallback((log) => {
    if (!log) return;
    setManagerConfig({ shiftLogId: log.id, readOnly: true });
    setShowManager(true);
  }, []);

  const handleEditShiftLog = useCallback((log) => {
    if (!log) return;
    setManagerConfig({ shiftLogId: log.id, readOnly: false });
    setShowManager(true);
  }, []);

  const handleDeleteShiftLog = useCallback(async (log) => {
    if (!log?.id) return;
    if (!window.confirm('Are you sure you want to delete this shift log?')) return;
    try {
      await deleteShiftLog(log.id);
      resetPagination();
    } catch (err) {
      console.error('Failed to delete shift log', err);
    }
  }, [resetPagination]);

  const handleManagerClose = useCallback(() => {
    setShowManager(false);
    setManagerConfig({ shiftLogId: null, readOnly: false });
    resetPagination();
  }, [resetPagination]);

  if (!isShiftLogModuleEnabled || !canViewShiftLogs) {
    const message = !isShiftLogModuleEnabled
      ? 'Shift Log module is disabled for this tenant.'
      : 'You do not have permission to view Shift Logs.';
    const variant = !isShiftLogModuleEnabled ? 'info' : 'danger';
    return (
      <div className="card h-100 p-0 rounded-0">
        <div className="card-body d-flex align-items-center justify-content-center">
          <div className={`alert alert-${variant} mb-0`} role="alert">
            {message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex flex-wrap align-items-center gap-3">
        {renderFilterControls()}
        {canCreateShiftLogs && (
          <div className="ms-auto">
            <button type="button" className="btn btn-primary text-sm py-12 px-16 radius-4" onClick={handleStartShiftLog}>
              Start Shift Log
            </button>
          </div>
        )}
      </div>
      <div className="card-body p-0 d-flex flex-column">
        <div className="d-flex justify-content-between align-items-center">
          <ul className="nav bordered-tab border border-top-0 border-start-0 border-end-0 d-inline-flex nav-pills mb-0">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'inProgress' ? 'active' : ''}`}
                onClick={() => handleTabChange('inProgress')}
              >
                In Progress
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'completed' ? 'active' : ''}`}
                onClick={() => handleTabChange('completed')}
              >
                Completed
              </button>
            </li>
          </ul>
        </div>
        <div className="flex-grow-1 d-flex flex-column">
          <div
            ref={listContainerRef}
            className="paginated-table-container scroll-sm flex-grow-1"
            style={{ height: 'calc(100vh - 275px)' }}
          >
            <ShiftLogDataTable
              logs={logs}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSortChange}
              onOpenShiftLog={handleOpenShiftLog}
              onViewShiftLog={handleViewShiftLog}
              onEditShiftLog={handleEditShiftLog}
              onDeleteShiftLog={handleDeleteShiftLog}
              loading={loading}
              timeZone={timeZone}
              activeTab={activeTab}
              permissions={{
                canView: canViewShiftLogs,
                canUpdate: canUpdateShiftLogs,
                canDelete: canDeleteShiftLogs,
              }}
            />
          </div>
        </div>
      </div>
      <ShiftLogManager
        show={showManager}
        onClose={handleManagerClose}
        userProfile={userProfile}
        initialShiftLogId={managerConfig.shiftLogId}
        initialReadOnly={managerConfig.readOnly}
      />
    </div>
    </>
  );
};

export default ShiftLogLayer;
